// PDP variant-switch speed fix (hkskincart-2, 2026-07-29).
//
// Measured on /products/fancl-serum-in-cover-foundation before this file:
// tapping a shade took ~2.5 s to show the new image, because stock Horizon
//   (a) re-fetches the ENTIRE product page on every variant change
//       (653 KB / ~2.0 s — reviews, brand products, recommendations and footer
//       all re-rendered for nothing), and then
//   (b) throws the whole gallery away (`media-gallery.replaceWith`), so the
//       newly selected shade's image starts downloading from zero (~0.46 s).
//
// Three additive fixes — stock `variant-picker.js` is NOT modified, so a
// Horizon upgrade cannot silently revert them:
//   1. request only the product-information section (653 KB -> 157 KB,
//      ~2.0 s -> ~0.6 s). Every component that reads the response
//      (product-price, product-inventory, product-sku, product-form,
//      sticky-add-to-cart, local-pickup, media-gallery) lives inside that
//      section, so nothing loses its update.
//   2. on the first sign of intent, read /products/<handle>.js once and warm
//      each variant's image in the HTTP cache, so the gallery swap has nothing
//      left to download.
//   3. prefetch the section HTML on hover / focus / pointer-down of a swatch
//      and serve the click that follows straight from that cache.
//
// Safety net (fix 1 can be refused by the platform): section rendering ignores
// the preview-theme COOKIE — `?section_id=…` returns 404 on an unpublished
// theme unless `preview_theme_id` is in the URL too, which is why that param is
// re-added below. Any other refusal (unknown template id, Shopify change) is
// caught at runtime: a section response that does not carry a variant picker is
// discarded, the full page is fetched instead, and section scoping switches off
// for the rest of the session. Worst case is stock Horizon behaviour, never a
// dead variant picker.
//
// Loaded on the product template only (snippets/scripts.liquid).

import VariantPicker from '@theme/variant-picker';

const proto = VariantPicker.prototype;
const originalBuildRequestUrl = proto.buildRequestUrl;
const originalFetchUpdatedSection = proto.fetchUpdatedSection;

/** Request URL -> Promise<string|null>. Null means "prefetch failed, fetch it for real". */
const htmlCache = new Map();
const HTML_CACHE_LIMIT = 12;

/** Section-scoped URL -> the full-page URL it replaced, for the fallback. */
const fullPageUrls = new Map();

/** Variant id -> featured image src. */
const variantImages = new Map();
const warmedImages = new Set();

/** Product URL -> Promise, so /products/<handle>.js is only ever read once. */
const productDataRequests = new Map();

/** Flipped to false the first time a section response comes back unusable. */
let sectionRenderingUsable = true;

// Respect Data Saver: the section request still gets smaller (fix 1), we just
// skip the speculative downloads.
const speculationAllowed = !navigator.connection?.saveData;

// On an unpublished (preview) theme the section must be requested explicitly —
// the preview cookie alone gives a 404.
const previewThemeId =
  window.Shopify?.theme && window.Shopify.theme.role !== 'main' ? window.Shopify.theme.id : null;

/* ------------------------------------------------------------------ *
 * 1) Section-scoped request instead of the whole product page
 * ------------------------------------------------------------------ */

/**
 * Rewrites a full-page variant request into a section-rendering request.
 * Returns the URL untouched whenever the full page is genuinely needed.
 * @param {Element} picker - The variant picker.
 * @param {HTMLElement} [selectedOption] - The option being selected.
 * @param {string} url - The URL stock Horizon built.
 * @returns {string} The URL to request.
 */
function sectionScopedUrl(picker, selectedOption, url) {
  if (!sectionRenderingUsable) return url;

  // Horizon already scopes quick-add, product cards and featured-product sections.
  if (!url || url.includes('section_id=')) return url;

  // Only the main PDP picker morphs this section; anything else keeps stock behaviour.
  if (picker.dataset.templateProductMatch !== 'true') return url;
  if (picker.closest('product-card') || picker.closest('quick-add-dialog')) return url;

  // A combined listing hopping to another product morphs <main> — that needs the whole page.
  const currentProductUrl = picker.dataset.productUrl?.split('?')[0];
  const connectedProductUrl = selectedOption?.dataset?.connectedProductUrl;
  if (connectedProductUrl && connectedProductUrl !== currentProductUrl) return url;

  const sectionId =
    picker.dataset.sectionId || picker.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
  if (!sectionId) return url;

  const [path, query] = url.split('?');
  const params = [`section_id=${encodeURIComponent(sectionId)}`];
  if (query) params.push(query);
  if (previewThemeId) params.push(`preview_theme_id=${previewThemeId}`);

  const scoped = `${path}?${params.join('&')}`;
  fullPageUrls.set(scoped, url);
  return scoped;
}

proto.buildRequestUrl = function (selectedOption, source = null, sourceSelectedOptionsValues = []) {
  const url = originalBuildRequestUrl.call(this, selectedOption, source, sourceSelectedOptionsValues);

  try {
    return sectionScopedUrl(this, selectedOption, url);
  } catch {
    return url;
  }
};

/* ------------------------------------------------------------------ *
 * 3) Serve the click from the prefetch cache (+ the safety net)
 * ------------------------------------------------------------------ */

/**
 * A usable response carries the variant picker's JSON island — that is what
 * stock `fetchUpdatedSection` reads before it updates anything.
 * @param {string|null} text - The response body.
 * @returns {boolean} Whether the body can drive a variant update.
 */
function isUsableSection(text) {
  return Boolean(text) && text.includes('<variant-picker');
}

proto.fetchUpdatedSection = function (requestUrl, morphElementSelector) {
  const fullPageUrl = fullPageUrls.get(requestUrl);
  const cached = htmlCache.get(requestUrl);

  // Nothing to add for un-scoped requests that were not prefetched.
  if (!fullPageUrl && !cached) return originalFetchUpdatedSection.call(this, requestUrl, morphElementSelector);

  // Feed the stock implementation a body without touching its abort / morph /
  // event logic. The stub only lives for this synchronous call.
  const realFetch = window.fetch;
  const stub = (input, init) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (url !== requestUrl) return realFetch(input, init);

    const body =
      cached ??
      realFetch(requestUrl, init)
        .then((response) => (response.ok ? response.text() : null))
        .catch(() => null);

    return body.then((text) => {
      if (!fullPageUrl) {
        // Un-scoped URL that happened to be prefetched — hand it over as-is.
        return text == null ? realFetch(input, init) : htmlResponse(text);
      }

      if (isUsableSection(text)) return htmlResponse(text);

      // Section rendering refused this request: fall back transparently and
      // stop scoping for the rest of the session.
      sectionRenderingUsable = false;
      htmlCache.delete(requestUrl);
      return realFetch(fullPageUrl, init);
    });
  };

  window.fetch = stub;
  try {
    return originalFetchUpdatedSection.call(this, requestUrl, morphElementSelector);
  } finally {
    if (window.fetch === stub) window.fetch = realFetch;
  }
};

/**
 * @param {string} text - An HTML body.
 * @returns {Response} A synthetic 200 response.
 */
function htmlResponse(text) {
  return new Response(text, { status: 200, headers: { 'Content-Type': 'text/html' } });
}

/**
 * Mirrors VariantPicker#buildRequestUrl for an option the shopper has not
 * clicked yet, so the prefetched key matches the URL the click will build.
 * @param {Element} picker - The variant picker.
 * @param {HTMLInputElement} input - The option being hovered.
 * @returns {string|null} The URL to prefetch, or null if it cannot be derived.
 */
function candidateRequestUrl(picker, input) {
  const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
  if (!fieldsets.length) return null;

  const values = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.dataset.optionValueId);
  const fieldsetIndex = Number.parseInt(input.dataset.fieldsetIndex ?? '', 10);
  if (Number.isNaN(fieldsetIndex) || fieldsetIndex >= values.length) return null;

  values[fieldsetIndex] = input.dataset.optionValueId;
  if (!values.every(Boolean)) return null;

  const productUrl = input.dataset.connectedProductUrl || picker.dataset.productUrl;
  if (!productUrl) return null;

  const params = [];
  const viewParam = new URLSearchParams(window.location.search).get('view');
  if (viewParam) params.push(`view=${viewParam}`);
  params.push(`option_values=${values.join(',')}`);

  return sectionScopedUrl(picker, input, `${productUrl}?${params.join('&')}`);
}

/**
 * @param {string|null} url - The section URL to warm.
 */
function prefetchSection(url) {
  if (!url || htmlCache.has(url)) return;

  const request = fetch(url, { priority: 'low' })
    .then((response) => (response.ok ? response.text() : null))
    .catch(() => null);

  htmlCache.set(url, request);
  if (htmlCache.size > HTML_CACHE_LIMIT) htmlCache.delete(htmlCache.keys().next().value);
}

/* ------------------------------------------------------------------ *
 * 2) Warm the variant images
 * ------------------------------------------------------------------ */

/**
 * @param {string} src - A Shopify CDN image src.
 * @param {number} width - The width to request.
 * @returns {string} The sized image URL.
 */
function withWidth(src, width) {
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

/**
 * Downloads a variant image at exactly the size the gallery would ask for, so
 * the swap after a variant change is a cache hit.
 * @param {string} [variantId] - The variant whose image to warm.
 */
function warmVariantImage(variantId) {
  const src = variantId ? variantImages.get(String(variantId)) : undefined;
  if (!src || warmedImages.has(src)) return;
  warmedImages.add(src);

  const template = document.querySelector('media-gallery slideshow-slide img');
  const widths = (template?.getAttribute('srcset') ?? '').match(/\d+(?=w)/g);
  const sizes = template?.getAttribute('sizes');

  const image = new Image();
  if (widths?.length && sizes) {
    // Same srcset and sizes as the gallery, so the browser picks the same candidate.
    image.sizes = sizes;
    image.srcset = widths.map((width) => `${withWidth(src, Number(width))} ${width}w`).join(', ');
  } else {
    image.src = withWidth(src, 1200);
  }
}

/**
 * Reads the variant -> image map once per product.
 * @param {HTMLElement} picker - The variant picker.
 */
function loadProductData(picker) {
  const productUrl = picker.dataset.productUrl;
  if (!productUrl || productDataRequests.has(productUrl)) return;

  const request = fetch(`${productUrl}.js`, { priority: 'low' })
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      for (const variant of product?.variants ?? []) {
        if (variant.featured_image?.src) variantImages.set(String(variant.id), variant.featured_image.src);
      }

      // The shopper is already on the picker, so warm every shade while idle.
      const warmAll = () => {
        for (const input of picker.querySelectorAll('input[data-variant-id]')) {
          warmVariantImage(input.dataset.variantId);
        }
      };

      if ('requestIdleCallback' in window) window.requestIdleCallback(warmAll, { timeout: 2000 });
      else setTimeout(warmAll, 500);
    })
    .catch(() => {});

  productDataRequests.set(productUrl, request);
}

/* ------------------------------------------------------------------ *
 * Intent tracking
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * 4) Make the swap visible
 * ------------------------------------------------------------------ */
//
// Once the gallery swaps instantly the change stops registering: six beige
// foundation shades look near-identical, so the new photo lands with no signal
// that anything happened. A short dissolve on the new image gives the eye
// something to catch without giving the speed back.

const SWAP_ANIMATION_MS = 300;

/** The gallery image showing at the moment the shopper picked, to detect a real change. */
let imageBeforeSwap = null;

// A plain <style> element rather than adoptedStyleSheets — this file must not
// throw on older Safari, or the speed fixes above would go down with it.
const swapStyles = document.createElement('style');
swapStyles.textContent = `
  @media (prefers-reduced-motion: no-preference) {
    media-gallery[data-swapped] slideshow-slides {
      animation: variant-media-swap ${SWAP_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    @keyframes variant-media-swap {
      from { opacity: 0.25; }
      to { opacity: 1; }
    }
  }
`;
document.head.append(swapStyles);

/**
 * @returns {string} The src of the image currently leading the gallery.
 */
function currentGalleryImage() {
  return document.querySelector('media-gallery slideshow-slide img')?.getAttribute('src')?.split('?')[0] ?? '';
}

document.addEventListener(
  'change',
  (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('variant-picker')) {
      imageBeforeSwap = currentGalleryImage();
    }
  },
  { capture: true }
);

// Runs after media-gallery's own handler: that one listens on the section, this
// one on the document, so by now the new gallery is already in place.
document.addEventListener('variant:update', () => {
  const gallery = document.querySelector('media-gallery');
  if (!gallery || imageBeforeSwap === null) return;

  // A size change that keeps the same photo should not flash.
  if (currentGalleryImage() === imageBeforeSwap) return;
  imageBeforeSwap = null;

  gallery.setAttribute('data-swapped', '');
  setTimeout(() => gallery.removeAttribute('data-swapped'), SWAP_ANIMATION_MS + 50);
});

// A pointer sweeping across six shades must not fire six section requests, so
// hovering only counts as intent after a short dwell. Pointer-down and focus
// are unambiguous and prefetch straight away.
const HOVER_DWELL_MS = 150;
let hoverTimer = 0;
/** @type {HTMLInputElement|null} */
let hoveredInput = null;

/**
 * @param {Element} picker - The variant picker.
 * @param {HTMLInputElement} input - The option to warm.
 */
function speculate(picker, input) {
  warmVariantImage(input.dataset.variantId);
  prefetchSection(candidateRequestUrl(picker, input));
}

/**
 * @param {Event} event - A pointerover / pointerdown / focusin event.
 */
function onIntent(event) {
  if (!speculationAllowed) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const picker = target.closest('variant-picker[data-template-product-match="true"]');
  if (!picker || picker.closest('product-card') || picker.closest('quick-add-dialog')) return;

  // The pointer is on the picker at all: worth the one small product read.
  loadProductData(/** @type {HTMLElement} */ (picker));

  const label = target.closest('label');
  const input =
    target instanceof HTMLInputElement
      ? target
      : label?.querySelector('input[type="radio"]') ??
        (label?.htmlFor ? document.getElementById(label.htmlFor) : null);

  if (!(input instanceof HTMLInputElement) || input.checked) return;

  if (event.type !== 'pointerover') {
    clearTimeout(hoverTimer);
    speculate(picker, input);
    return;
  }

  if (hoveredInput === input) return;
  hoveredInput = input;
  clearTimeout(hoverTimer);
  hoverTimer = window.setTimeout(() => speculate(picker, input), HOVER_DWELL_MS);
}

for (const type of ['pointerover', 'pointerdown', 'focusin']) {
  document.addEventListener(type, onIntent, { passive: true });
}
